// Implements: .claude/skills/rehaut-design/ui_kits/_shared/primitives.jsx (Icon)
// See: .claude/skills/rehaut-design/HANDOFF.md
//
// Inline SVG icon set. Per the brief, "no icon library" — every glyph
// in the system flows through this single component, drawn against a
// 16×16 viewBox at 1.4px stroke weight. Add new entries to PATHS as
// they're needed; do not pull from lucide / heroicons / etc.

import type { CSSProperties } from 'react';

export type IconName =
  | 'search'
  | 'plus'
  | 'chevron'
  | 'chevronr'
  | 'chevronl'
  | 'close'
  | 'user'
  | 'phone'
  | 'wa'
  | 'mail'
  | 'calendar'
  | 'note'
  | 'package'
  | 'filter'
  | 'sparkle'
  | 'edit'
  | 'arrow_r'
  | 'settings'
  | 'check'
  | 'sort';

const PATHS: Record<IconName, string> = {
  search:   'M11 11l4 4M7 13a6 6 0 1 1 0-12 6 6 0 0 1 0 12z',
  plus:     'M8 3v10M3 8h10',
  chevron:  'M5 7l3 3 3-3',
  chevronr: 'M6 4l4 4-4 4',
  chevronl: 'M10 4l-4 4 4 4',
  close:    'M4 4l8 8M12 4l-8 8',
  user:     'M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM2 14c0-3 2.7-5 6-5s6 2 6 5',
  phone:    'M3 3l2-1 2 3-2 1a8 8 0 0 0 4 4l1-2 3 2-1 2c-4 1-9-4-9-9z',
  wa:       'M3 13l1-3a5 5 0 1 1 2 2l-3 1z',
  mail:     'M2 4h12v8H2zM2 4l6 4 6-4',
  calendar: 'M3 5h10v9H3zM3 5V3M13 5V3M3 8h10',
  note:     'M4 2h6l3 3v9H4zM10 2v3h3',
  package:  'M8 2l5 3v6L8 14 3 11V5zM3 5l5 3 5-3M8 8v6',
  filter:   'M2 3h12L9 9v5l-2-1V9z',
  sparkle:  'M8 2l1.5 4.5L14 8l-4.5 1.5L8 14l-1.5-4.5L2 8l4.5-1.5z',
  edit:     'M11 2l3 3-8 8H3v-3z',
  arrow_r:  'M3 8h10M9 4l4 4-4 4',
  settings: 'M8 5a3 3 0 1 0 0 6 3 3 0 0 0 0-6zM8 1v2M8 13v2M14 8h-2M4 8H2M12.7 3.3l-1.4 1.4M4.7 11.3l-1.4 1.4M12.7 12.7l-1.4-1.4M4.7 4.7L3.3 3.3',
  check:    'M3 8l3 3 7-7',
  sort:     'M4 5l3-3 3 3M4 11l3 3 3-3',
};

export function Icon({
  name,
  size = 14,
  color = 'currentColor',
  style,
  ariaLabel,
}: {
  name: IconName;
  size?: number;
  color?: string;
  style?: CSSProperties;
  /** Provide for actionable icons; omit (defaults aria-hidden) for decorative ones. */
  ariaLabel?: string;
}) {
  const ariaProps = ariaLabel
    ? { role: 'img' as const, 'aria-label': ariaLabel }
    : { 'aria-hidden': true as const };
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke={color}
      strokeWidth={1.4}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={style}
      {...ariaProps}
    >
      <path d={PATHS[name]} />
    </svg>
  );
}
